import { useMutationDeleteLead, useMutationEditLead } from "@gymflow/api";
import { FormikProvider, useFormik } from "formik";
import { apiV2, queryClient } from "libs/api/src/lib/apiV2";
import { useQueryLead } from "libs/api/src/lib/hooks/lead/useQueryLead";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { usePortalRoutes } from "../../../hooks";
import { ModalContext } from "../../../providers";
import { ToastContext } from "../../../providers/ToastProvider/context";
import { RouteFeature, RouteLayout } from "../../../routes";
import useGymflowModels from "../../../store";
import { Button, FormInput } from "../../atoms";
import { NewUserSideBarProviderContext } from "../../molecules";
import { ConfirmModal } from "../../templates";
import { LeadEmailSchema } from "./LeadEmailSchema";

export const LeadEmailForm = ({ leadId }: { leadId: number }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { createClubLink } = usePortalRoutes();
  const { api } = useGymflowModels();
  const { data: lead } = useQueryLead({ api, leadId });
  const { mutateAsync: deleteLead } = useMutationDeleteLead({ api });
  const { mutateAsync: updateLead } = useMutationEditLead({ api });
  const formik = useFormik({
    initialValues: {
      email: lead?.email,
    },
    validationSchema: toFormikValidationSchema(
      LeadEmailSchema(
        lead?.email ?? "",
        async (email) =>
          await queryClient.fetchQuery(
            apiV2.hosted.member.queries.emailExists(email),
          ),
      ),
    ),
    onSubmit: async (values) => {
      await updateLead({
        id: leadId,
        patchedFields: { email: values.email },
      });
    },
  });
  const { setModal, hide } = useContext(ModalContext);
  const { toast } = useContext(ToastContext);
  const { open } = useContext(NewUserSideBarProviderContext);
  return (
    <FormikProvider value={formik}>
      <div className="flex w-full flex-col">
        <div className="flex w-full flex-col gap-y-2 p-6">
          <div className="flex flex-col gap-y-2">
            <FormInput
              name="email"
              placeholder={t("pages.lead.profile.email")}
              label={t("pages.lead.profile.email")}
            />
            <div className="text-xs text-gray-600">
              {t("pages.lead.profile.noAccountDescription")}
            </div>
          </div>
        </div>
        <div className="border-b border-gray-200" />
        <div className="flex w-full flex-row flex-wrap justify-end gap-4 p-6">
          <Button
            intent="link-error"
            onClick={() => {
              setModal(
                <ConfirmModal
                  type="danger"
                  title={t("pages.lead.profile.deleteLead")}
                  onCancel={hide}
                  cancelText={t("portal.common.cancel")}
                  confirmText={t("portal.common.delete")}
                  onConfirm={async () => {
                    await deleteLead({ leadId });
                    toast({ message: t("pages.lead.profile.userDeleted") });
                    history.push(
                      createClubLink(RouteLayout.Staff, RouteFeature.LeadList),
                    );
                    hide();
                  }}
                >
                  {t("pages.lead.profile.deleteLeadConfirmation")}
                </ConfirmModal>,
              );
            }}
          >
            {t("portal.common.delete")}
          </Button>

          <Button
            intent="default"
            onClick={() => {
              open({
                creationMode: "MEMBER",
                defaultFormValues: {
                  email: lead?.email,
                  mobileNumber: lead?.mobileNumber,
                  firstName: lead?.firstName,
                  lastName: lead?.lastName,
                },
                onClose: (arg) => {
                  if (arg?.userType === "MEMBER") {
                    history.push(
                      createClubLink(
                        RouteLayout.Staff,
                        RouteFeature.UserMember.replace(
                          ":id",
                          arg.userMemberId,
                        ),
                      ),
                    );
                  }
                },
              });
            }}
          >
            {t("pages.lead.profile.setupAccount")}
          </Button>

          <Button
            intent="secondary"
            onClick={async () => {
              await formik.submitForm();
            }}
            disabled={!formik.dirty}
          >
            {t("portal.common.save")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
